import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { IconButton, TextField } from "@mui/material";
import { formatUtilizationAmount } from "./utilizationReportUtils";

const getCertificationParagraphs = (data) => {
  if (Array.isArray(data?.certificationColumnParagraphs)) {
    return data.certificationColumnParagraphs;
  }

  if (data?.certificationColumnText) {
    return [data.certificationColumnText];
  }

  return [];
};

const UtilizationUcReportDraft = ({
  data,
  checksExercised = [""],
  onCheckChange,
  onCheckAdd,
  onCheckRemove,
  certificateIndex = 1,
  certificateCount = 1,
}) => {
  if (!data) return null;

  const checks = checksExercised.length > 0 ? checksExercised : [""];
  const tableRows = data.tableRows || [];
  const certificationParagraphs = getCertificationParagraphs(data);
  const certRowSpan = tableRows.length + 2;
  const showCertificateLabel = certificateCount > 1;

  const renderCertificationColumn = () =>
    certificationParagraphs.map((paragraph, paragraphIndex) => {
      const text =
        typeof paragraph === "string" ? paragraph : paragraph?.text || "";
      const variant =
        typeof paragraph === "object" ? paragraph?.variant : null;

      return (
        <p
          key={`cert-para-${paragraphIndex}`}
          className={`uc-draft__cert-para${
            variant ? ` uc-draft__cert-para--${variant}` : ""
          }`}
        >
          {text}
        </p>
      );
    });

  return (
    <div className="uc-draft">
      {showCertificateLabel && (
        <p className="uc-draft__certificate-label">
          Certificate {certificateIndex} of {certificateCount}
        </p>
      )}
      <div className="uc-draft__header">
        <h2 className="uc-draft__title">{data.formTitle}</h2>
        <p className="uc-draft__line">{data.ruleReference}</p>
        <p className="uc-draft__line uc-draft__line--bold">{data.formSubtitle}</p>
        <p className="uc-draft__line uc-draft__line--bold">{data.formCondition}</p>
        <p className="uc-draft__line uc-draft__line--bold">
          Project Name-{" "}
          <span className="uc-draft__dynamic">{data.projectName || "-"}</span>
        </p>
        <p className="uc-draft__line uc-draft__line--bold">
          Department -{" "}
          <span className="uc-draft__dynamic">{data.departmentName}</span>
        </p>
        <p className="uc-draft__line uc-draft__line--bold">
          Financial Year-
          <span className="uc-draft__dynamic">{data.selectedFy}</span> as on{" "}
          <span className="uc-draft__dynamic">{data.selectedMonth}</span>
        </p>
        <p className="uc-draft__line uc-draft__line--bold">
          Grant Code - <span className="uc-draft__dynamic">{data.grantCode || "-"}</span>
        </p>
        <p className="uc-draft__line uc-draft__line--bold uc-draft__dynamic">
          {data.schemeNameLine}
        </p>
      </div>

      <div className="uc-draft__table-section">
        <table className="uc-draft__table">
          <colgroup>
            <col className="uc-draft__col uc-draft__col--sr" />
            <col className="uc-draft__col uc-draft__col--letter" />
            <col className="uc-draft__col uc-draft__col--subhead" />
            <col className="uc-draft__col uc-draft__col--amount" />
            <col className="uc-draft__col uc-draft__col--cert" />
          </colgroup>
          <tbody>
            <tr className="uc-draft__table-head-row">
              <td className="uc-draft__head-cell">Sr. No.</td>
              <td className="uc-draft__head-cell">Letter No and Date</td>
              <td className="uc-draft__head-cell">Subhead</td>
              <td className="uc-draft__head-cell">Amount</td>
              <td rowSpan={certRowSpan} className="uc-draft__cert-column">
                {renderCertificationColumn()}
              </td>
            </tr>

            {tableRows.map((row, index) => (
              <tr key={`${row.srNo}-${index}`}>
                <td className="uc-draft__cell-center">{row.srNo}</td>
                <td>{row.letterNoAndDate}</td>
                <td>{row.subHeadName}</td>
                <td className="uc-draft__cell-right">
                  {formatUtilizationAmount(row.amount)}
                </td>
              </tr>
            ))}

            <tr className="uc-draft__total-row">
              <td colSpan={3} className="uc-draft__total-label">
                Total
              </td>
              <td className="uc-draft__cell-right">
                {formatUtilizationAmount(data.totalAmount)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="uc-draft__certification">{data.certificationText}</p>

      <div className="uc-draft__checks-section">
        <p className="uc-draft__checks-heading">Kind of Checks exercised:</p>
        <ol className="uc-draft__checks-inputs">
          {checks.map((value, index) => (
            <li key={`check-${index}`} className="uc-draft__check-row">
              <span className="uc-draft__check-number">{index + 1}.</span>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={1}
                placeholder={`Enter check ${index + 1}`}
                value={value}
                onChange={(e) => onCheckChange(index, e.target.value)}
                className="uc-draft__check-input"
              />
              <div className="uc-draft__check-actions">
                {checks.length > 1 && (
                  <IconButton
                    type="button"
                    size="small"
                    className="uc-draft__check-action-btn uc-draft__check-action-btn--remove"
                    onClick={() => onCheckRemove(index)}
                    aria-label={`Remove check ${index + 1}`}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                )}
                {index === checks.length - 1 && (
                  <IconButton
                    type="button"
                    size="small"
                    className="uc-draft__check-action-btn uc-draft__check-action-btn--add"
                    onClick={onCheckAdd}
                    aria-label="Add check"
                  >
                    <AddCircleOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default UtilizationUcReportDraft;
